import { cardState, dayInfo, loggedCycles } from './cycleModel';
import { indexCravingLogs, indexEnergyLogs, indexPeriodLogs, indexSymptomLogs } from './dailyLogs';
import { addDays, daysBetween } from './dates';
import { mean } from './format';

const byStart = (list) => {
  const out = {};
  (list || []).forEach((c) => {
    if (c?.start_date) out[c.start_date] = c;
  });
  return out;
};

/**
 * Everything the view models read, built once per fetch.
 * @param {{ today: string, historyStart: string, summaries: object, logs: object }} data - useHormoneData's payload.
 */
export function buildContext(data) {
  const { today, historyStart, summaries = {}, logs = {} } = data;
  const P = summaries.period || {};
  const cycles = loggedCycles(P, today);
  const cur = cycles[cycles.length - 1] || null;
  const done = cycles.filter((c) => c.completed);
  const periods = P.periods || [];

  const ctx = {
    today,
    historyStart,
    period: P,
    cycles,
    cur,
    done,
    prev: done[done.length - 1] || null,
    latestPeriod: periods[periods.length - 1] || null,
    nextPeriod: P.next_period || null,
    state: cardState(P, today),
    analytics: {
      pms: byStart(summaries.symptoms?.cycles_analytics),
      energy: byStart(summaries.energy?.cycles_analytics),
      cravings: byStart(summaries.cravings?.cycles_analytics),
    },
    logs: {
      period: indexPeriodLogs(logs.period),
      pms: indexSymptomLogs(logs.symptoms),
      energy: indexEnergyLogs(logs.energy),
      cravings: indexCravingLogs(logs.cravings),
    },
  };
  ctx.info = (iso) => dayInfo(cycles, iso, today);
  ctx.hasData = cycles.length > 0;
  return ctx;
}

/** Today's cycle day (the server's, else counted from the current cycle's start). */
export const cycleDayToday = (ctx) =>
  ctx.period.cycle_day ?? (ctx.cur ? daysBetween(ctx.cur.start, ctx.today) + 1 : null);

/**
 * Pain over one period, from the raw logs (README gap G3: the summary has the
 * average only). Days after today are never counted.
 */
export function painStats(ctx, start, end) {
  const days = [];
  for (let d = start; d <= end && d <= ctx.today; d = addDays(d, 1)) days.push(d);
  let hi = null;
  let logged = 0;
  let zero = 0;
  const values = [];
  days.forEach((d) => {
    const v = ctx.logs.period[d]?.pain;
    if (v == null) return;
    logged += 1;
    values.push(v);
    if (v === 0) zero += 1;
    if (hi == null || v > hi.v) hi = { v, date: d };
  });
  return { days, logged, zero, hi, avg: mean(values) };
}

/** A cycle's luteal span from the server's phases, cut at today; null until it has begun. */
export function lutealSpan(ctx, c) {
  const span = c?.phases.find((p) => p.phase === 'luteal');
  if (!span || span.start_date > ctx.today) return null;
  const end = span.end_date < ctx.today ? span.end_date : ctx.today;
  return { start: span.start_date, end, days: daysBetween(span.start_date, end) + 1 };
}

/**
 * A cycle's luteal-phase stats, shaped like a window: the server's
 * `by_phase.luteal` over the server's luteal span. PMS and Cravings read this
 * in place of the pre-period window (gap G2). The phase is an estimate
 * (ovulation = next start − 14), so the span is ~13 days whatever the client's
 * real PMS days are.
 */
export function lutealOf(ctx, metric, c) {
  if (!c) return null;
  const span = lutealSpan(ctx, c);
  const stats = ctx.analytics[metric][c.start]?.by_phase?.luteal;
  if (!span || !stats) return null;
  return {
    start_date: span.start,
    end_date: span.end,
    provisional: !c.completed,
    average: stats.average ?? null,
    days_logged: stats.logged_days ?? 0,
    active_days: stats.active_days ?? 0,
    days_total: span.days,
    by_symptom: stats.by_symptom,
    by_category: stats.by_category,
  };
}

/** A cycle's phase average for one metric (server `by_phase`), or null. */
export const phaseAverage = (ctx, metric, c, phase) =>
  (c ? ctx.analytics[metric][c.start]?.by_phase?.[phase]?.average ?? null : null);

/** The latest completed cycle's luteal window, with its cycle and the window before it. */
export function latestLuteal(ctx, metric) {
  for (let i = ctx.done.length - 1; i >= 0; i--) {
    const w = lutealOf(ctx, metric, ctx.done[i]);
    if (w) return { w, cycle: ctx.done[i], prevW: i > 0 ? lutealOf(ctx, metric, ctx.done[i - 1]) : null };
  }
  return null;
}

/** The previous luteal window's average, or null. */
export const previousWindowAverage = (found) => found?.prevW?.average ?? null;

/** By-item rows of a window, normalised across symptoms (`by_symptom`) and cravings (`by_category`). */
export const windowItems = (w) =>
  (w?.by_symptom || w?.by_category || []).map((it) => ({
    name: it.name,
    average: it.average ?? it.average_intensity ?? null,
    days: it.days ?? 0,
  }));
